import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';
import { OrnamentalDivider } from '../ui/Decorations';
interface PersonalDataPhaseProps {
  onNext: (data: {
    nombre_completo: string;
    fecha_nacimiento: string;
    signo_zodiacal: string;
  }) => void;
}
const signos = ['Aries', 'Tauro', 'Géminis', 'Cáncer', 'Leo', 'Virgo', 'Libra', 'Escorpio', 'Sagitario', 'Capricornio', 'Acuario', 'Piscis'];
// Alchemical "Eye of the Manuscript" sigil — minimalist mystic eye-in-circle
const AlchemicalEye = () => <svg width="56" height="56" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="mx-auto mb-5" data-id="element-181">
    {/* outer circle */}
    <circle cx="32" cy="32" r="29" stroke="#D4AF37" strokeWidth="0.8" opacity="0.7" data-id="element-182" />
    {/* inner thin ring */}
    <circle cx="32" cy="32" r="24" stroke="#D4AF37" strokeWidth="0.4" opacity="0.5" data-id="element-183" />
    {/* eye almond shape */}
    <path d="M14 32 Q32 18 50 32 Q32 46 14 32 Z" stroke="#D4AF37" strokeWidth="0.9" fill="none" data-id="element-184" />
    {/* iris */}
    <circle cx="32" cy="32" r="6" stroke="#D4AF37" strokeWidth="0.8" fill="rgba(212,175,55,0.08)" data-id="element-185" />
    {/* pupil */}
    <circle cx="32" cy="32" r="2" fill="#D4AF37" data-id="element-186" />
    {/* tiny rays */}
    <line x1="32" y1="3" x2="32" y2="9" stroke="#D4AF37" strokeWidth="0.6" data-id="element-187" />
    <line x1="32" y1="55" x2="32" y2="61" stroke="#D4AF37" strokeWidth="0.6" data-id="element-188" />
    <line x1="3" y1="32" x2="9" y2="32" stroke="#D4AF37" strokeWidth="0.6" data-id="element-189" />
    <line x1="55" y1="32" x2="61" y2="32" stroke="#D4AF37" strokeWidth="0.6" data-id="element-190" />
  </svg>;
const meses = [{
  value: '01',
  label: 'Enero'
}, {
  value: '02',
  label: 'Febrero'
}, {
  value: '03',
  label: 'Marzo'
}, {
  value: '04',
  label: 'Abril'
}, {
  value: '05',
  label: 'Mayo'
}, {
  value: '06',
  label: 'Junio'
}, {
  value: '07',
  label: 'Julio'
}, {
  value: '08',
  label: 'Agosto'
}, {
  value: '09',
  label: 'Septiembre'
}, {
  value: '10',
  label: 'Octubre'
}, {
  value: '11',
  label: 'Noviembre'
}, {
  value: '12',
  label: 'Diciembre'
}];
const dias = Array.from({
  length: 31
}, (_, i) => String(i + 1).padStart(2, '0'));
const currentYear = new Date().getFullYear();
const anios = Array.from({
  length: 90
}, (_, i) => String(currentYear - 18 - i));
export const PersonalDataPhase = ({
  onNext
}: PersonalDataPhaseProps) => {
  const [formData, setFormData] = useState({
    nombre_completo: '',
    fecha_nacimiento: '',
    signo_zodiacal: ''
  });
  const [birthDate, setBirthDate] = useState({
    dia: '',
    mes: '',
    anio: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Combine date parts → YYYY-MM-DD whenever any of them changes
  const updateBirthDate = (next: {
    dia: string;
    mes: string;
    anio: string;
  }) => {
    setBirthDate(next);
    if (next.dia && next.mes && next.anio) {
      setFormData(prev => ({
        ...prev,
        fecha_nacimiento: `${next.anio}-${next.mes}-${next.dia}`
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        fecha_nacimiento: ''
      }));
    }
  };
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.nombre_completo.trim()) newErrors.nombre_completo = 'El nombre es indispensable para el registro.';
    if (!formData.fecha_nacimiento) newErrors.fecha_nacimiento = 'La fecha de nacimiento es requerida.';
    if (!formData.signo_zodiacal) newErrors.signo_zodiacal = 'Seleccione su signo zodiacal.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onNext(formData);
    }
  };
  // Minimalist underline input style — 0.5px gold bottom border only
  const underlineInputClasses = 'w-full bg-transparent border-0 border-b border-gold/40 hover:border-gold/70 focus:border-gold rounded-none px-1 py-2.5 text-parchment font-sans text-base focus:outline-none transition-colors placeholder-parchment/30';
  const labelClasses = 'block font-sans uppercase mb-2';
  const labelStyle = {
    color: '#b8923d',
    fontSize: '11px',
    letterSpacing: '0.22em',
    fontWeight: 500 as const
  };
  return <motion.div className="w-full max-w-2xl mx-auto px-4 py-8 flex flex-col justify-center min-h-screen" initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.6
  }} data-id="element-191">
      <ProgressBar progress={15} label="Fase 1: Apertura" data-id="element-192" />

      <div className="text-center mt-10 mb-10" data-id="element-193">
        <AlchemicalEye data-id="element-194" />
        <h2 className="font-cinzel mb-5 tracking-wide" style={{
        color: '#D4AF37',
        fontSize: 'clamp(22px, 4vw, 30px)',
        fontWeight: 600
      }} data-id="element-195">
          PROTOCOLO DE APERTURA DE LECTURA
        </h2>
        <p className="font-sans mx-auto max-w-xl" style={{
        color: '#E0D4BC',
        fontSize: '14px',
        lineHeight: 1.6,
        fontWeight: 400
      }} data-id="element-196">
          Una lectura seria requiere precisión. Usamos tus datos de nacimiento
          para que el Oráculo identifique tu posición exacta en el destino,
          eliminando cualquier margen de error en tu devolución final.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-7 px-2 md:px-6" data-id="element-197">
        <div data-id="element-198">
          <label className={labelClasses} style={labelStyle} data-id="element-199">
            Nombre Completo
          </label>
          <input type="text" value={formData.nombre_completo} onChange={e => setFormData({
          ...formData,
          nombre_completo: e.target.value
        })} className={underlineInputClasses} placeholder="Ingrese su nombre completo para el registro..." data-id="element-200" />
          {errors.nombre_completo && <p className="text-burgundy-light text-xs mt-2 font-sans" data-id="element-201">
              {errors.nombre_completo}
            </p>}
        </div>

        <div data-id="element-202">
          <label className={labelClasses} style={labelStyle} data-id="element-203">
            Fecha de Nacimiento
          </label>
          <div className="grid grid-cols-3 gap-3" data-id="element-204">
            <select value={birthDate.dia} onChange={e => updateBirthDate({
            ...birthDate,
            dia: e.target.value
          })} className={`${underlineInputClasses} appearance-none cursor-pointer pr-6`} style={{
            backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23D4AF37' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 4px center'
          }} data-id="element-205">
              <option value="" disabled className="bg-ink text-parchment/60" data-id="element-206">
                Día
              </option>
              {dias.map(d => <option key={d} value={d} className="bg-ink text-parchment" data-id="element-207">
                  {d}
                </option>)}
            </select>

            <select value={birthDate.mes} onChange={e => updateBirthDate({
            ...birthDate,
            mes: e.target.value
          })} className={`${underlineInputClasses} appearance-none cursor-pointer pr-6`} style={{
            backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23D4AF37' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 4px center'
          }} data-id="element-208">
              <option value="" disabled className="bg-ink text-parchment/60" data-id="element-209">
                Mes
              </option>
              {meses.map(m => <option key={m.value} value={m.value} className="bg-ink text-parchment" data-id="element-210">
                  {m.label}
                </option>)}
            </select>

            <select value={birthDate.anio} onChange={e => updateBirthDate({
            ...birthDate,
            anio: e.target.value
          })} className={`${underlineInputClasses} appearance-none cursor-pointer pr-6`} style={{
            backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23D4AF37' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 4px center'
          }} data-id="element-211">
              <option value="" disabled className="bg-ink text-parchment/60" data-id="element-212">
                Año
              </option>
              {anios.map(a => <option key={a} value={a} className="bg-ink text-parchment" data-id="element-213">
                  {a}
                </option>)}
            </select>
          </div>
          {errors.fecha_nacimiento && <p className="text-burgundy-light text-xs mt-2 font-sans" data-id="element-214">
              {errors.fecha_nacimiento}
            </p>}
        </div>

        <div data-id="element-215">
          <label className={labelClasses} style={labelStyle} data-id="element-216">
            Signo Zodiacal
          </label>
          <select value={formData.signo_zodiacal} onChange={e => setFormData({
          ...formData,
          signo_zodiacal: e.target.value
        })} className={`${underlineInputClasses} appearance-none cursor-pointer pr-6`} style={{
          backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23D4AF37' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 4px center'
        }} data-id="element-217">
            <option value="" disabled className="bg-ink text-parchment/60" data-id="element-218">
              Seleccione su signo...
            </option>
            {signos.map(s => <option key={s} value={s} className="bg-ink text-parchment" data-id="element-219">
                {s}
              </option>)}
          </select>
          {errors.signo_zodiacal && <p className="text-burgundy-light text-xs mt-2 font-sans" data-id="element-220">
              {errors.signo_zodiacal}
            </p>}
        </div>

        <OrnamentalDivider className="py-6" data-id="element-221" />

        <Button type="submit" fullWidth className="py-4 btn-ritual-pulse" data-id="element-222">
          Siguiente paso
          <ArrowRight className="w-5 h-5 ml-2" data-id="element-223" />
        </Button>
      </form>
    </motion.div>;
};